import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Stack
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  ViewList as ViewListIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { progressStorageService } from '../../services/progressStorageService';
import { fetchAdvisorConfig } from '../../services/advisorConfigService';
import { useNavigate } from 'react-router-dom';

interface FormEntry {
  _id: string;
  sessionId: string;
  customerId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  currentStep: number;
  stepName: string;
  formData: any;
  createdAt: string;
  updatedAt: string;
  lastUpdated: string;
  lastSaved?: string;
  isComplete?: boolean;
}

interface FieldStatus {
  name: string;
  value: any;
  isFilled: boolean;
  displayValue: string;
}

interface SectionStatus {
  name: string;
  fields: FieldStatus[];
  completionPercentage: number;
}

const CompletedFormsView: React.FC = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormEntry[]>([]);
  const [filteredForms, setFilteredForms] = useState<FormEntry[]>([]);
  const [selectedForm, setSelectedForm] = useState<FormEntry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'json' | 'fields'>('fields');
  const [advisorNames, setAdvisorNames] = useState<Map<string, string>>(new Map());

  // Helper function to check if a value is filled
  const isFieldFilled = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'number') return value > 0;
    if (typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return false;
  };

  // Analyze form data and return section status
  const analyzeFormData = (formData: any): SectionStatus[] => {
    const sections: SectionStatus[] = [];

    // Personal Information Section
    const personalFields: FieldStatus[] = [];
    const personal = formData?.personal || {};

    personalFields.push({
      name: 'First Name',
      value: personal.client1?.firstName,
      isFilled: isFieldFilled(personal.client1?.firstName),
      displayValue: personal.client1?.firstName || ''
    });

    personalFields.push({
      name: 'Last Name',
      value: personal.client1?.lastName,
      isFilled: isFieldFilled(personal.client1?.lastName),
      displayValue: personal.client1?.lastName || ''
    });

    personalFields.push({
      name: 'Date of Birth',
      value: personal.client1?.dateOfBirth,
      isFilled: isFieldFilled(personal.client1?.dateOfBirth),
      displayValue: personal.client1?.dateOfBirth || ''
    });

    personalFields.push({
      name: 'Email',
      value: personal.contact?.email,
      isFilled: isFieldFilled(personal.contact?.email),
      displayValue: personal.contact?.email || ''
    });

    personalFields.push({
      name: 'Phone',
      value: personal.contact?.phone,
      isFilled: isFieldFilled(personal.contact?.phone),
      displayValue: personal.contact?.phone || ''
    });

    if (personal.hasPartner) {
      personalFields.push({
        name: 'Partner First Name',
        value: personal.client2?.firstName,
        isFilled: isFieldFilled(personal.client2?.firstName),
        displayValue: personal.client2?.firstName || ''
      });

      personalFields.push({
        name: 'Partner Last Name',
        value: personal.client2?.lastName,
        isFilled: isFieldFilled(personal.client2?.lastName),
        displayValue: personal.client2?.lastName || ''
      });

      personalFields.push({
        name: 'Partner Date of Birth',
        value: personal.client2?.dateOfBirth,
        isFilled: isFieldFilled(personal.client2?.dateOfBirth),
        displayValue: personal.client2?.dateOfBirth || ''
      });
    }

    const personalFilledCount = personalFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Personal Information',
      fields: personalFields,
      completionPercentage: Math.round((personalFilledCount / personalFields.length) * 100)
    });

    // Employment & Income Section
    const employmentFields: FieldStatus[] = [];
    const employment = formData?.employment || {};

    employmentFields.push({
      name: 'Is Retired',
      value: employment.client1?.isRetired,
      isFilled: employment.client1?.isRetired !== undefined,
      displayValue: employment.client1?.isRetired ? 'Yes' : 'No'
    });

    employmentFields.push({
      name: 'Current Income',
      value: employment.client1?.currentIncome,
      isFilled: isFieldFilled(employment.client1?.currentIncome),
      displayValue: employment.client1?.currentIncome ? `$${employment.client1.currentIncome.toLocaleString()}` : ''
    });

    employmentFields.push({
      name: 'Expected Retirement Age',
      value: employment.client1?.expectedRetirementAge,
      isFilled: isFieldFilled(employment.client1?.expectedRetirementAge),
      displayValue: employment.client1?.expectedRetirementAge?.toString() || ''
    });

    if (personal.hasPartner) {
      employmentFields.push({
        name: 'Partner Is Retired',
        value: employment.client2?.isRetired,
        isFilled: employment.client2?.isRetired !== undefined,
        displayValue: employment.client2?.isRetired ? 'Yes' : 'No'
      });

      employmentFields.push({
        name: 'Partner Income',
        value: employment.client2?.currentIncome,
        isFilled: isFieldFilled(employment.client2?.currentIncome),
        displayValue: employment.client2?.currentIncome ? `$${employment.client2.currentIncome.toLocaleString()}` : ''
      });

      employmentFields.push({
        name: 'Partner Expected Retirement Age',
        value: employment.client2?.expectedRetirementAge,
        isFilled: isFieldFilled(employment.client2?.expectedRetirementAge),
        displayValue: employment.client2?.expectedRetirementAge?.toString() || ''
      });
    }

    const employmentFilledCount = employmentFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Employment & Income',
      fields: employmentFields,
      completionPercentage: Math.round((employmentFilledCount / employmentFields.length) * 100)
    });

    // Social Security & Pensions Section
    const ssFields: FieldStatus[] = [];
    const socialSecurity = formData?.socialSecurity || {};

    ssFields.push({
      name: 'Client SS Current Amount',
      value: socialSecurity.client1?.currentAmount,
      isFilled: isFieldFilled(socialSecurity.client1?.currentAmount),
      displayValue: socialSecurity.client1?.currentAmount ? `$${socialSecurity.client1.currentAmount.toLocaleString()}` : ''
    });

    ssFields.push({
      name: 'Client SS Projected Amount',
      value: socialSecurity.client1?.projectedAmount,
      isFilled: isFieldFilled(socialSecurity.client1?.projectedAmount),
      displayValue: socialSecurity.client1?.projectedAmount ? `$${socialSecurity.client1.projectedAmount.toLocaleString()}` : ''
    });

    if (personal.hasPartner) {
      ssFields.push({
        name: 'Partner SS Current Amount',
        value: socialSecurity.client2?.currentAmount,
        isFilled: isFieldFilled(socialSecurity.client2?.currentAmount),
        displayValue: socialSecurity.client2?.currentAmount ? `$${socialSecurity.client2.currentAmount.toLocaleString()}` : ''
      });

      ssFields.push({
        name: 'Partner SS Projected Amount',
        value: socialSecurity.client2?.projectedAmount,
        isFilled: isFieldFilled(socialSecurity.client2?.projectedAmount),
        displayValue: socialSecurity.client2?.projectedAmount ? `$${socialSecurity.client2.projectedAmount.toLocaleString()}` : ''
      });
    }

    const ssFilledCount = ssFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Social Security & Pensions',
      fields: ssFields,
      completionPercentage: Math.round((ssFilledCount / ssFields.length) * 100)
    });

    // Savings & Investments Section
    const savingsFields: FieldStatus[] = [];
    const savings = formData?.savings || {};

    // Helper function to calculate total from array based on owner
    const getAssetTotal = (assets: any[], owner?: string): number => {
      if (!Array.isArray(assets)) return 0;
      if (owner) {
        return assets.filter(a => a.owner === owner).reduce((sum, a) => sum + (a.balance || 0), 0);
      }
      return assets.reduce((sum, a) => sum + (a.balance || 0), 0);
    };

    const taxDeferredValue = getAssetTotal(savings.taxDeferred || []);
    const taxFreeValue = getAssetTotal(savings.taxFree || []);
    const taxableValue = getAssetTotal(savings.taxable || []);

    savingsFields.push({
      name: 'Tax Deferred Assets',
      value: savings.taxDeferred,
      isFilled: taxDeferredValue > 0,
      displayValue: taxDeferredValue > 0 ? `$${taxDeferredValue.toLocaleString()}` : ''
    });

    savingsFields.push({
      name: 'Tax Free Assets',
      value: savings.taxFree,
      isFilled: taxFreeValue > 0,
      displayValue: taxFreeValue > 0 ? `$${taxFreeValue.toLocaleString()}` : ''
    });

    savingsFields.push({
      name: 'Taxable Assets',
      value: savings.taxable,
      isFilled: taxableValue > 0,
      displayValue: taxableValue > 0 ? `$${taxableValue.toLocaleString()}` : ''
    });

    if (personal.hasPartner) {
      const partnerTaxDeferredValue = getAssetTotal(savings.taxDeferred || [], 'client2');
      const partnerTaxFreeValue = getAssetTotal(savings.taxFree || [], 'client2');
      const jointTaxableValue = getAssetTotal(savings.taxable || [], 'joint');

      savingsFields.push({
        name: 'Partner Tax Deferred Assets',
        value: partnerTaxDeferredValue,
        isFilled: partnerTaxDeferredValue > 0,
        displayValue: partnerTaxDeferredValue > 0 ?
          `$${partnerTaxDeferredValue.toLocaleString()}` : ''
      });

      savingsFields.push({
        name: 'Partner Tax Free Assets',
        value: partnerTaxFreeValue,
        isFilled: partnerTaxFreeValue > 0,
        displayValue: partnerTaxFreeValue > 0 ?
          `$${partnerTaxFreeValue.toLocaleString()}` : ''
      });

      savingsFields.push({
        name: 'Joint Taxable Assets',
        value: jointTaxableValue,
        isFilled: jointTaxableValue > 0,
        displayValue: jointTaxableValue > 0 ?
          `$${jointTaxableValue.toLocaleString()}` : ''
      });
    }

    const savingsFilledCount = savingsFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Savings & Investments',
      fields: savingsFields,
      completionPercentage: Math.round((savingsFilledCount / savingsFields.length) * 100)
    });

    // Expenses Section
    const expensesFields: FieldStatus[] = [];
    const expensesInfo = formData?.expensesInfo || {};
    
    expensesFields.push({
      name: 'Current Annual Expenses',
      value: expensesInfo.currentAnnualExpenses,
      isFilled: isFieldFilled(expensesInfo.currentAnnualExpenses),
      displayValue: expensesInfo.currentAnnualExpenses ? 
        `$${expensesInfo.currentAnnualExpenses.toLocaleString()}` : ''
    });
    
    expensesFields.push({
      name: 'Expected Retirement Expenses',
      value: expensesInfo.expectedRetirementExpenses,
      isFilled: isFieldFilled(expensesInfo.expectedRetirementExpenses),
      displayValue: expensesInfo.expectedRetirementExpenses ? 
        `$${expensesInfo.expectedRetirementExpenses.toLocaleString()}` : ''
    });
    
    expensesFields.push({
      name: 'Has Expected Changes',
      value: expensesInfo.expectedExpenseChanges?.hasChanges,
      isFilled: expensesInfo.expectedExpenseChanges?.hasChanges !== undefined,
      displayValue: expensesInfo.expectedExpenseChanges?.hasChanges ? 'Yes' : 'No'
    });

    const expensesFilledCount = expensesFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Expenses',
      fields: expensesFields,
      completionPercentage: Math.round((expensesFilledCount / expensesFields.length) * 100)
    });

    // Goals Section
    const goalsFields: FieldStatus[] = [];
    const goals = formData?.goals || {};

    goalsFields.push({
      name: 'Has Specific Goals',
      value: goals.hasSpecificGoals,
      isFilled: goals.hasSpecificGoals !== undefined,
      displayValue: goals.hasSpecificGoals ? 'Yes' : 'No'
    });

    goalsFields.push({
      name: 'Target Income',
      value: goals.targetIncome,
      isFilled: isFieldFilled(goals.targetIncome),
      displayValue: goals.targetIncome ? `$${goals.targetIncome.toLocaleString()}` : ''
    });

    // Selected goals as a single field
    const selectedGoals = goals.selected || {};
    const goalsList = Object.entries(selectedGoals)
      .filter(([_, selected]) => selected)
      .map(([goal, _]) => goal.charAt(0).toUpperCase() + goal.slice(1).replace(/([A-Z])/g, ' $1'))
      .join(', ');
    
    goalsFields.push({
      name: 'Selected Goals',
      value: goalsList,
      isFilled: goalsList.length > 0,
      displayValue: goalsList || 'None selected'
    });

    const goalsFilledCount = goalsFields.filter(f => f.isFilled).length;
    sections.push({
      name: 'Goals',
      fields: goalsFields,
      completionPercentage: Math.round((goalsFilledCount / goalsFields.length) * 100)
    });

    return sections;
  };

  const loadForms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const completedSessions = await progressStorageService.getCompletedSessions();
      const completedForms: FormEntry[] = completedSessions.map((session: any) => ({
        _id: session._id || session.sessionId,
        sessionId: session.sessionId,
        customerId: session.customerId,
        firstName: session.formData?.personal?.client1?.firstName || session.firstName,
        lastName: session.formData?.personal?.client1?.lastName || session.lastName,
        email: session.formData?.personal?.contact?.email || session.email,
        phone: session.formData?.personal?.contact?.phone || session.phone,
        currentStep: session.currentStep,
        stepName: session.stepName,
        formData: session.formData,
        createdAt: session.createdAt,
        updatedAt: session.lastUpdated || session.updatedAt || session.createdAt,
        lastUpdated: session.lastUpdated || session.updatedAt || session.createdAt,
        lastSaved: session.lastSaved,
        isComplete: session.isComplete
      }));
      setForms(completedForms);
      setFilteredForms(completedForms);
    } catch (err) {
      console.error('Error loading forms:', err);
      setError('Failed to load completed forms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  // Bulk-fetch advisor names for all unique customer IDs
  useEffect(() => {
    const fetchAdvisorNames = async () => {
      const uniqueIds = Array.from(new Set(forms.map(f => f.customerId).filter(Boolean))) as string[];
      const names = new Map<string, string>();
      await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const config = await fetchAdvisorConfig(id);
            if (config?.name) names.set(id, config.name);
          } catch { /* ignore */ }
        })
      );
      setAdvisorNames(names);
    };
    if (forms.length > 0) fetchAdvisorNames();
  }, [forms]);

  useEffect(() => {
    const filtered = forms.filter(form => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (form.firstName && form.firstName.toLowerCase().includes(searchLower)) ||
        (form.lastName && form.lastName.toLowerCase().includes(searchLower)) ||
        (form.email && form.email.toLowerCase().includes(searchLower)) ||
        (form.phone && form.phone.includes(searchTerm)) ||
        (form.customerId && form.customerId.includes(searchTerm)) ||
        (form.sessionId && form.sessionId.toLowerCase().includes(searchLower))
      );
    });
    setFilteredForms(filtered);
  }, [searchTerm, forms]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAdvisorName = (customerId?: string) => {
    if (!customerId) return 'Unknown Advisor';
    return advisorNames.get(customerId) || 'Unknown Advisor';
  };

  const handleViewDetails = (form: FormEntry) => {
    setSelectedForm(form);
    setViewDialogOpen(true);
  };

  const handleDelete = async (sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      try {
        await progressStorageService.deleteSession(sessionId);
        await loadForms();
      } catch (error) {
        console.error('Failed to delete form:', error);
        alert('Failed to delete form. Please try again.');
      }
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminLoginTime');
    navigate('/admin');
  };

  const exportToCSV = () => {
    const headers = ['Session ID', 'Name', 'Email', 'Phone', 'Advisor Name', 'Customer ID', 'Status', 'Last Updated'];
    const rows = filteredForms.map(form => [
      form.sessionId,
      `${form.firstName || ''} ${form.lastName || ''}`.trim(),
      form.email || '',
      form.phone || '',
      getAdvisorName(form.customerId),
      form.customerId || '',
      'Completed',
      new Date(form.lastUpdated).toISOString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `completed-forms-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading completed forms...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Completed Forms
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={() => navigate('/admin/incomplete-forms')}
              variant="outlined"
            >
              View Incomplete Forms
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadForms}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              variant="contained"
              disabled={filteredForms.length === 0}
            >
              Export CSV
            </Button>
            <Button
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              variant="outlined"
              color="error"
            >
              Logout
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email, phone, or customer ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {filteredForms.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              {searchTerm ? 'No forms match your search' : 'No completed forms found'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Advisor Name</TableCell>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredForms.map((form) => (
                  <TableRow key={form.sessionId}>
                    <TableCell>
                      {form.firstName || form.lastName ? (
                        `${form.firstName || ''} ${form.lastName || ''}`.trim()
                      ) : (
                        <em>Not provided</em>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        {form.email && (
                          <Typography variant="body2">{form.email}</Typography>
                        )}
                        {form.phone && (
                          <Typography variant="body2" color="textSecondary">
                            {form.phone}
                          </Typography>
                        )}
                        {!form.email && !form.phone && (
                          <em>Not provided</em>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{getAdvisorName(form.customerId)}</TableCell>
                    <TableCell>{form.customerId || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label="Completed"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(form.lastUpdated)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(form)}
                        title="View details"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(form.sessionId)}
                        title="Delete form"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Form Details</Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="fields">
                <ViewListIcon sx={{ mr: 1 }} />
                Field Overview
              </ToggleButton>
              <ToggleButton value="json">
                <CodeIcon sx={{ mr: 1 }} />
                Raw JSON
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedForm && (
            viewMode === 'json' ? (
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', overflow: 'auto', maxHeight: '70vh' }}>
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '12px' }}>
                  {JSON.stringify(selectedForm.formData, null, 2)}
                </pre>
              </Paper>
            ) : (
              <Box>
                {/* Session Info */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Session Information</Typography>
                  <Typography variant="body2">Session ID: {selectedForm.sessionId}</Typography>
                  <Typography variant="body2">Customer ID: {selectedForm.customerId || 'N/A'}</Typography>
                  <Typography variant="body2">Created: {formatDate(selectedForm.createdAt)}</Typography>
                  <Typography variant="body2">Last Updated: {formatDate(selectedForm.lastUpdated)}</Typography>
                </Box>

                {/* Form Sections */}
                {analyzeFormData(selectedForm.formData).map((section) => (
                  <Box key={section.name} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                        {section.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {section.fields.filter(f => f.isFilled).length} of {section.fields.length} fields provided
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 2 }}>
                      {section.fields.map((field) => (
                        <Box key={field.name} sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          py: 0.5,
                          borderBottom: '1px solid #f0f0f0'
                        }}>
                          <Typography variant="body2" sx={{ 
                            minWidth: '200px',
                            fontWeight: 'medium',
                            color: field.isFilled ? 'text.primary' : 'text.secondary'
                          }}>
                            {field.name}:
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            flex: 1,
                            color: field.isFilled ? 'text.primary' : 'text.secondary',
                            fontStyle: field.isFilled ? 'normal' : 'italic'
                          }}>
                            {field.displayValue || 'Not provided'}
                          </Typography>
                          {field.isFilled ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50', ml: 1 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 16, color: '#f44336', ml: 1 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompletedFormsView;